import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'

export interface OnlineOrderingGate {
  /** true only when settings have loaded AND this tenant has the add-on. */
  enabled: boolean
  /** true only while the first fetch is in flight — render neutral UI
   *  (skeleton), never the paywall, so paying tenants don't see it flash. */
  isLoading: boolean
  /** true when `['settings','all']` failed to load — the consumer should show
   *  a retry state, NOT hang on the skeleton or flash the paywall (E-P3). */
  isError: boolean
}

/**
 * useOnlineOrderingEnabled tells the UI whether this tenant has the premium
 * online-ordering add-on (backend env `FEATURE_ONLINE_ORDERING=1`, surfaced
 * as the server-injected `feature_online_ordering` key on GET /settings —
 * env-authoritative, never stored in app_settings).
 *
 *   • enabled  → normal online-orders inbox, sidebar badge, counter alert
 *   • disabled → premium paywall + golden lock on the nav item; the backend
 *                also 403s (`feature_not_enabled`) all online-ordering APIs
 *
 * Shares the `['settings','all']` query cache with the rest of the app, so
 * this adds no extra network call. A missing key (old backend) reads as
 * disabled — per-tenant FE+BE deploy together, so that skew is transient.
 */
export function useOnlineOrderingEnabled(): OnlineOrderingGate {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: Infinity,
  })
  const all = data?.data as Record<string, unknown> | undefined
  return {
    enabled: all?.feature_online_ordering === true,
    // react-query's isLoading is true ONLY on the first fetch with no data; on
    // error it flips to false, so the consumer stops the skeleton (E-P3 — the
    // old `all === undefined` hung forever when GET /settings 500'd).
    isLoading,
    isError,
  }
}
